import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { productErrorApiService } from "../../../service/admin/productErrorApiService";
import { adminApiService } from "../../../service/admin/adminApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import PriceCalc from "./ProductErrorPriceCalc";
import ApplyAllModal from "./ApplyAllModal";
import Logo from "../../../website/assets/images/common/logo_no_image.png";
import "./ProductErrorDetail.css";

export default function ProductErrorDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProductId, setActiveProductId] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showPriceCalc, setShowPriceCalc] = useState(false);
  const [showApplyAllModal, setShowApplyAllModal] = useState(false);
  const [applyAllResults, setApplyAllResults] = useState([]);
  const [applyAllProduct, setApplyAllProduct] = useState(null);
  const [collapsedProducts, setCollapsedProducts] = useState({});
  const [applyAllDimensions, setApplyAllDimensions] = useState({});
  const [isSingleVariantMode, setIsSingleVariantMode] = useState(false);















  const requestedIds = useMemo(() => {
    if (id) return [id];
    const idsParam = searchParams.get("ids") || "";
    return idsParam
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }, [id, searchParams]);

  const activeProduct = useMemo(
    () => products.find((item) => String(item._id) === String(activeProductId)) || null,
    [products, activeProductId]
  );

  useEffect(() => {
    fetchProductDetails();
  }, [requestedIds.join(",")]);

  const fetchProductDetails = async () => {
    if (!requestedIds.length) {
      message.showToast("No product selected", ToastTypes.WARNING);
      navigate("/admin/product-errors");
      return;
    }

    try {
      setLoading(true);
      
      // Use bulk API for single or multiple products
      const response = await adminApiService.getProductErrorsBulkDetails(requestedIds);
      
      if (!response?.data?.products) {
        message.showToast("Failed to load product details", ToastTypes.ERROR);
        navigate("/admin/product-errors");
        return;
      }

      // Extract only the found products from the bulk response
      const loadedProducts = response.data.products
        .filter(item => item.found && item.data)
        .map(item => item.data);

      if (!loadedProducts.length) {
        message.showToast("No valid products found", ToastTypes.ERROR);
        navigate("/admin/product-errors");
        return;
      }

      // Show warning for missing products if any
      const missingCount = response.data.summary?.missing || 0;
      if (missingCount > 0) {
        message.showToast(
          `${missingCount} product(s) not found. Loaded ${loadedProducts.length} product(s).`,
          ToastTypes.WARNING
        );
      }

      setProducts(loadedProducts);
      setActiveProductId((prev) => prev || loadedProducts[0]?._id || null);
    } catch (error) {
      console.error("Failed to fetch product details:", error);
      message.showToast("Failed to load product details", ToastTypes.ERROR);
      navigate("/admin/product-errors");
    } finally {
      setLoading(false);
    }
  };

  const handleVariantUpdate = (productId, variant) => {
    setActiveProductId(productId);
    setSelectedVariant(variant);
    setShowPriceCalc(true);
  };

  const handleApplyPrice = async (finalPrice, wholesaleFinalPrice) => {
    if (!selectedVariant) return;
    
    try {
      
      // Build payload for single variant update
      const payload = {
        skuId: selectedVariant.skuId || selectedVariant.sku,
        update: {
          finalPrice: Math.round(finalPrice),
          finalPriceMRP: Math.round(finalPrice * 2.5),
          wholesaleFinalPrice: wholesaleFinalPrice ? Math.round(wholesaleFinalPrice) : null,
          wholesaleFinalPriceMRP: wholesaleFinalPrice ? Math.round(wholesaleFinalPrice * 2.5) : null,
        }
      };
      
      await productErrorApiService.updateProductErrorVariant(activeProductId, payload);
      message.showToast("Variant price updated successfully", ToastTypes.SUCCESS);
      
      // Refresh product data
      await fetchProductDetails();
      setShowPriceCalc(false);
      setSelectedVariant(null);
    } catch (error) {
      console.error("Failed to update variant:", error);
      message.showToast(
        error?.status?.message || error?.message || "Failed to update variant price",
        ToastTypes.ERROR
      );
    } finally {
      loader.hideLoader();
    }
  };

  const handleApplyDimensions = async (dimensions) => {
    if (!selectedVariant) return;
    
    try {
      // Build payload for dimension update
      const payload = {
        skuId: selectedVariant.skuId || selectedVariant.sku,
        update: {
          weight: dimensions.weight,
          length: dimensions.length,
          height: dimensions.height,
          width: dimensions.width,
        }
      };
      
      await productErrorApiService.updateProductErrorVariant(activeProductId, payload);
      
      // Refresh product data
      await fetchProductDetails();
    } catch (error) {
      console.error("Failed to update dimensions:", error);
      message.showToast(
        error?.response?.data?.message || "Failed to update dimensions",
        ToastTypes.ERROR
      );
    }
  };

  const handleApplyToAllStart = () => {
    setApplyAllResults([]);
  };

  const handleApplyToAllProgress = () => {};

  const handleApplyToAllComplete = (results, dimensions) => {
    setApplyAllResults(results);
    setApplyAllProduct(activeProduct);
    setApplyAllDimensions(dimensions || {});
    setIsSingleVariantMode(false);
    setShowApplyAllModal(true);
  };

  const handleSingleVariantModal = (results, dimensions) => {
    setApplyAllResults(results);
    setApplyAllProduct(activeProduct);
    setApplyAllDimensions(dimensions || {});
    setIsSingleVariantMode(true);
    setShowApplyAllModal(true);
  };

  const handleConfirmApplyAll = async (results, dimensions) => {
    try {
      // loader.showLoader();
      
      if (isSingleVariantMode && results.length === 1) {
        // Handle single variant update
        const result = results[0];
        const payload = {
          skuId: result.variantSkuId || result.variantKey,
          update: {
            finalPrice: Math.round(result.finalPrice),
            finalPriceMRP: Math.round(result.finalPrice * 2.5),
            wholesaleFinalPrice: result.wholesaleFinalPrice ? Math.round(result.wholesaleFinalPrice) : null,
            weight: dimensions.weight,
            length: dimensions.length,
            height: dimensions.height,
            width: dimensions.width,
          }
        };
        
        await productErrorApiService.updateProductErrorVariant(activeProductId, payload);
        message.showToast("Variant price updated successfully", ToastTypes.SUCCESS);
      } else {
        // Handle apply to all variants
        const payload = {
          productSP: Math.min(...results.map(r => r.finalPrice).filter(p => p != null && Number.isFinite(p) && p > 0)),
          editedWeightDimensions: {},
          pricingMode: "dropshipping",
          remark: "Price or Dimensions Update from Product Error",
        };
        
        // Add all variant updates to payload
        results.forEach((result) => {
          const variantKey = result.variantKey;
          if (variantKey) {
            payload.editedWeightDimensions[variantKey] = {
              weight: dimensions.weight,
              length: dimensions.length,
              height: dimensions.height,
              width: dimensions.width,
              finalPrice: result.finalPrice,
              finalPriceMRP: result.finalPrice ? Math.round(result.finalPrice * 2.5) : null,
              wholesaleFinalPrice: result.wholesaleFinalPrice,
            };
          }
        });
        
        console.log("Applying to all variants with payload:", payload);
        
        // Use new apply-to-all-variants API
        const response = await productErrorApiService.applyToAllVariants(applyAllProduct?._id, payload);
        
        if (response?.status?.code === 200) {
          message.showToast(`Successfully updated ${results.length} variants and product pricing`, ToastTypes.SUCCESS);
        } else {
          throw new Error(response?.status?.message || "Failed to apply updates");
        }
      }
      
      // Refresh product data
      await fetchProductDetails();
      setShowApplyAllModal(false);
      setShowPriceCalc(false);
      setSelectedVariant(null);
      setApplyAllResults([]);
      setApplyAllProduct(null);
      setApplyAllDimensions({});
      setIsSingleVariantMode(false);
    } catch (error) {
      console.error("Failed to apply updates:", error);
      message.showToast(
        error?.response?.data?.message || error?.message || "Failed to apply updates",
        ToastTypes.ERROR
      );
    } finally {
      loader.hideLoader();
    }
  };

    const toggleProductCollapse = (productId) => {
    setCollapsedProducts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const normalizeUrl = (url) => {
    if (!url) return "";
    try {
      return String(url).replace(/^http:\/\//, "https://");
    } catch {
      return url;
    }
  };

  const formatPrice = (value, symbol = "₹") => {
    if (value == null || !Number.isFinite(Number(value))) return "-";
    const num = Number(value);
    return `${symbol}${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  if (loading) {
    return (
      <div className="ped-container">
        <div className="ped-loading">Loading product details...</div>
      </div>
    );
  }

  if (!products.length) {
    return (
      <div className="ped-container">
        <div className="ped-error">Products not found</div>
      </div>
    );
  }

  return (
    <div className="ped-container">
      <div className="ped-header">
        <button className="ped-back-btn" onClick={() => navigate("/admin/product-errors")}>
          ← Back to List
        </button>
        <h1>Product Error Details</h1>
      </div>

      {products.map((product) => {
        const productImage = product.imgUrl?.[0] ? normalizeUrl(product.imgUrl[0]) : Logo;
        const variations = product.variations || [];
        
       const collapsed = !!collapsedProducts[product._id];
        return (
          <div className="ped-main-card" key={product._id}>
            <div className="ped-product-section">
              <div className="ped-product-card ped-product-card-compact">
                <div className="ped-product-image-container">
                  <img
                    src={productImage}
                    alt={product.title || "Product"}
                    className="ped-product-image"
                    onError={(e) => {
                      e.target.src = Logo;
                    }}
                  />
                </div>
                <div className="ped-product-info">
                  <div className="ped-product-title">{product.title || "No Title"}</div>
                  <div className="ped-product-details">
                    <div className="ped-detail-row">
                      <span className="ped-detail-label">SKU:</span>
                      <span className="ped-detail-value">{product.sku || product.parentSku || "-"}</span>
                    </div>
                    <div className="ped-detail-row">
                      <span className="ped-detail-label">Total Variants:</span>
                      <span className="ped-detail-value">{variations.length}</span>
                    </div>
                    <div className="ped-detail-row">
                      <span className="ped-detail-label">Status:</span>
                      <span className="ped-detail-value">{product.status || product.processingStatus || "-"}</span>
                    </div>
                  </div>
                </div>
                <button
                     className="ped-collapse-btn"
                     onClick={() => toggleProductCollapse(product._id)}
                   >
                     {collapsed ? "Expand" : "Collapse"}
                   </button>
              </div>
            </div>
            {!collapsed && (
              <div className="ped-variants-section ped-variants-section-scroll">
                <div className="ped-variants-header">
                  <h2>All Variants ({variations.length})</h2>
                </div>
               <div className="ped-variant-table ped-variant-table-scroll">
                  <div className="ped-variant-row ped-variant-head">
                    <div>Image</div>
                    <div>SKU / Specifications</div>
                    <div>Pricing</div>
                    <div>Dimensions</div>
                    <div>Action</div>
                  </div>
                  {variations.map((variant, index) => {
                    const variantImage = variant.imgUrl?.[0] || variant.image || productImage;
                    const attributes = variant.skuAttributes || [];
                    return (
                      <div key={variant.skuId || variant.sku || index} className="ped-variant-row">
                        <div className="ped-col-image">
                          <img
                            src={normalizeUrl(variantImage)}
                            alt={`Variant ${index + 1}`}
                            className="ped-variant-thumb"
                            onError={(e) => {
                              e.target.src = Logo;
                            }}
                          />
                        </div>
                        <div className="ped-col-sku-spec">
                          <div className="ped-variant-sku">SKU: {variant.skuId || variant.sku || "-"}</div>
                          <div className="ped-variant-attributes">
                            {attributes.length ? attributes.map((attr, attrIdx) => (
                              <div key={attrIdx} className="ped-attribute">
                                <span className="ped-attribute-name">{attr.attributeNameTrans || attr.attributeName}:</span>{" "}
                                <span className="ped-attribute-value">{attr.valueTrans || attr.value}</span>
                              </div>
                            )) : <span className="ped-attribute-value">No specs</span>}
                          </div>
                        </div>
                        <div className="ped-col-pricing">
                          <div className="ped-price-row">
                            <span className="ped-price-label">AP:</span>
                            <span className="ped-price-value">{variant.ap || variant.actualPrice ? `¥${variant.ap || variant.actualPrice}` : "-"}</span>
                            <span className="ped-price-label">{"  ,  "}DS Final Price:</span>
                            <span className="ped-price-value">{formatPrice(variant.finalPrice)}</span>
                          </div>
                          <div className="ped-price-row">
                            <span className="ped-price-label">CP:</span>
                            <span className="ped-price-value">{variant.cp || variant.consignPrice ? `¥${variant.cp || variant.consignPrice}` : "-"}</span>
                            <span className="ped-price-label">{"  , "}WS Final Price:</span>
                            <span className="ped-price-value">{formatPrice(variant.wholesaleFinalPrice)}</span>
                          </div>
                        </div>
                        <div className="ped-col-dimensions">
                          <div className="ped-dimension-row">
                            <span className="ped-dimension-label">Weight:</span>
                            <span className="ped-dimension-value">{variant.weight || 0} kg</span>
                          </div>
                          <div className="ped-dimension-row">
                            <span className="ped-dimension-label">L x W x H:</span>
                            <span className="ped-dimension-value">{variant.length || 0} x {variant.width || 0} x {variant.height || 0}</span>
                          </div>
                        </div>
                        <div className="ped-col-action">
                          <button
                            className="ped-update-btn"
                            onClick={() => handleVariantUpdate(product._id, variant)}
                          >
                            Update Variant
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {showPriceCalc && selectedVariant && activeProduct && (
        <PriceCalc
          open={showPriceCalc}
          onClose={() => {
            setShowPriceCalc(false);
            setSelectedVariant(null);
          }}
          variation={selectedVariant}
          onApplyPrice={handleApplyPrice}
          onApplyDimensions={handleApplyDimensions}
          localShipping={activeProduct.local_shipping || 0}
          defaultCategoryId={activeProduct.categoryId}
          pricingMode="dropshipping"
          allVariants={activeProduct.variations || []}
          onApplyToAllVariants={(dimensions, pricesByVariant) => {
            console.log("Apply to all variants", dimensions, pricesByVariant);
          }}
          onApplyToAllStart={handleApplyToAllStart}
          onApplyToAllProgress={handleApplyToAllProgress}
          onApplyToAllComplete={handleApplyToAllComplete}
          onShowSingleVariantModal={handleSingleVariantModal}
        />
      )}

      {showApplyAllModal && applyAllResults.length > 0 && applyAllProduct && (
        <ApplyAllModal
          open={showApplyAllModal}
          onClose={() => {
            setShowApplyAllModal(false);
            setApplyAllResults([]);
            setApplyAllProduct(null);
            setApplyAllDimensions({});
            setIsSingleVariantMode(false);
          }}
          product={applyAllProduct}
          results={applyAllResults}
          dimensions={applyAllDimensions}
          onConfirm={handleConfirmApplyAll}
          isSingleVariant={isSingleVariantMode}
        />
      )}
    </div>
  );
}


